import React, { useMemo } from 'react';
import ReactSwitch from 'react-switch';

import { ImageSquare, VideoCamera, PlayBox, Eye, Plus, Printer } from 'src/common/icons';
import { Crop } from 'src/common/icons';
import FaceSearchIcon from 'src/common/icons/FaceSearch';
import SvgChatAI from 'src/common/icons/ChatAI';
import SvgWrench from 'src/common/icons/Wrench';
import PuzzlePiece from 'src/common/icons/PuzzlePiece';
import useModal from 'src/hooks/useModal';
import AddonSettingsModal from 'src/components/AddonSettingsModal';
import { useAlerts } from 'src/common/AlertManager';
import {
  useUpdateAdminUserSettingsMutation,
  useAddAdminUserAddonsMutation,
  useRemoveAdminUserAddonsMutation,
  useUpdateAdminUserAddonSettingsMutation,
} from 'src/api/mutations/adminUserSettingsMutations';

import styles from '../styles.module.scss';

const SUPPORTED_ADDONS = new Set([
  'ON_DEMAND_IMAGE',
  'VIDEO_TRANSCODE',
  'STORYBOARD',
  'FACE_RECOGNITION',
  'IMAGE_ANALYSIS',
  'REMOVE_BACKGROUND',
  'AI_ASSISTANT',
  'AI_IMAGE_EDIT',
  'PRINT_EXPORT',
]);

const BUILTIN_ADDONS = new Set(['ON_DEMAND_IMAGE', 'VIDEO_TRANSCODE', 'STORYBOARD']);

const ALWAYS_ENABLED_ADDONS = new Set(['ON_DEMAND_IMAGE', 'VIDEO_TRANSCODE']);

// Addons that expose a configurable trigger-criteria UI (gear icon).
// ODI is excluded per spec; VIDEO_TRANSCODE and STORYBOARD don't take criteria.
const CONFIGURABLE_ADDONS = new Set([
  'FACE_RECOGNITION',
  'IMAGE_ANALYSIS',
  'REMOVE_BACKGROUND',
  'PRINT_EXPORT',
  'EMOTION_CLASSIFICATION',
  'SCENE_CLASSIFICATION',
  'POSE_CLASSIFICATION',
]);

const ADDON_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ON_DEMAND_IMAGE: ImageSquare,
  VIDEO_TRANSCODE: VideoCamera,
  STORYBOARD: PlayBox,
  FACE_RECOGNITION: FaceSearchIcon,
  IMAGE_ANALYSIS: Eye,
  REMOVE_BACKGROUND: Crop,
  AI_ASSISTANT: SvgChatAI,
  AI_IMAGE_EDIT: ImageSquare,
  PRINT_EXPORT: Printer,
};

interface AddonsTabProps {
  userId: number;
  userSettings: any;
  adminSettings: any;
}

const AddonsTab: React.FC<AddonsTabProps> = ({ userId, userSettings, adminSettings }) => {
  const { showSuccess, showAlert } = useAlerts();
  const { showModal } = useModal();

  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);
  const addAddonsMutation = useAddAdminUserAddonsMutation(userId);
  const removeAddonsMutation = useRemoveAdminUserAddonsMutation(userId);
  const updateAddonSettingsMutation = useUpdateAdminUserAddonSettingsMutation(userId);

  const userAddons = useMemo(() => {
    if (!userSettings?.addons) return [];
    return Object.entries(userSettings.addons)
      .map(([key, value]: [string, any]) => ({ key, ...value }))
      .filter((addon) => addon.added && SUPPORTED_ADDONS.has(addon.key));
  }, [userSettings?.addons]);

  const adminAddons = useMemo(() => {
    if (!adminSettings?.addons) return {};
    return adminSettings.addons;
  }, [adminSettings?.addons]);

  const availableToAdd = useMemo(() => {
    const userAddonKeys = new Set(userAddons.map((a) => a.key));
    return Object.entries(adminAddons)
      .filter(([key, value]: [string, any]) => value.added && SUPPORTED_ADDONS.has(key) && !userAddonKeys.has(key))
      .map(([key, value]: [string, any]) => ({ key, ...value }));
  }, [adminAddons, userAddons]);

  const handleToggleAddon = async (addonKey: string, enabled: boolean) => {
    try {
      const addonsPayload = { ...userSettings.addons };
      addonsPayload[addonKey] = { ...addonsPayload[addonKey], enabled };
      await updateSettingsMutation.mutateAsync({ key: 'addons', value: addonsPayload });
      showSuccess(`Addon ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      showAlert('Failed to update addon');
    }
  };

  const handleAddAddon = async (addonKey: string) => {
    try {
      await addAddonsMutation.mutateAsync({ [addonKey]: { added: true, enabled: true } });
      showSuccess(`Addon added`);
    } catch {
      showAlert('Failed to add addon. You may not have this addon on your own account.');
    }
  };

  const handleRemoveAddon = async (addonKey: string) => {
    try {
      await removeAddonsMutation.mutateAsync([addonKey]);
      showSuccess(`Addon removed`);
    } catch {
      showAlert('Failed to remove addon');
    }
  };

  const handleSaveAddonSettings = async (addonKey: string, eventHooks: any) => {
    try {
      const data = { 'event-hooks': eventHooks };
      await updateAddonSettingsMutation.mutateAsync({ addonKey, data });
      showSuccess('Addon settings updated');
    } catch (e) {
      showAlert('Failed to update addon settings');
      throw e;
    }
  };

  const openSettingsModal = (addon: { key: string; title?: string }) => {
    const initialEventHooks = userSettings?.addons?.[addon.key]?.settings?.['event-hooks'];
    showModal(
      <AddonSettingsModal
        addonKey={addon.key}
        addonTitle={addon.title}
        initialEventHooks={initialEventHooks}
        onSave={(eventHooks) => handleSaveAddonSettings(addon.key, eventHooks)}
      />,
    );
  };

  return (
    <div>
      {userAddons.length === 0 ? (
        <div className={styles.emptyState}>No addons configured for this user.</div>
      ) : (
        <div className={styles.addonCardGrid}>
          {userAddons.map((addon) => {
            const IconComponent = ADDON_ICONS[addon.key] || PuzzlePiece;
            const isBuiltIn = BUILTIN_ADDONS.has(addon.key);
            const isAlwaysEnabled = ALWAYS_ENABLED_ADDONS.has(addon.key);
            const isConfigurable = CONFIGURABLE_ADDONS.has(addon.key);

            return (
              <div key={addon.key} className={styles.addonCard}>
                <div className={styles.addonCardIcon}>
                  <IconComponent />
                </div>
                <div className={styles.addonCardInfo}>
                  <h4>{addon.title || addon.key}</h4>
                  {addon.description && <p>{addon.description}</p>}
                </div>
                <div className={styles.addonCardActions}>
                  <ReactSwitch
                    checked={addon.enabled}
                    onChange={(checked: boolean) => handleToggleAddon(addon.key, checked)}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    disabled={isAlwaysEnabled || updateSettingsMutation.isLoading}
                    onColor="#2e53e3"
                    width={40}
                    height={20}
                    handleDiameter={16}
                  />
                  {isConfigurable && (
                    <div className={styles.gearHolder} onClick={() => openSettingsModal(addon)}>
                      <SvgWrench style={{ width: 14, height: 14, cursor: 'pointer' }} />
                    </div>
                  )}
                  {!isBuiltIn && (
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemoveAddon(addon.key)}
                      disabled={removeAddonsMutation.isLoading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {availableToAdd.length > 0 && (
        <div className={styles.addAddonSection}>
          <h4 className={styles.addAddonHeader}>Add Addons</h4>
          <div className={styles.addAddonGrid}>
            {availableToAdd.map((addon) => (
              <button
                key={addon.key}
                className={styles.addAddonChip}
                onClick={() => handleAddAddon(addon.key)}
                disabled={addAddonsMutation.isLoading}
              >
                <Plus style={{ width: 12, height: 12 }} />
                {addon.title || addon.key}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonsTab;
